import { Badge, Group, Text, Tooltip, ActionIcon } from '@mantine/core'
import { IconLink } from '@tabler/icons-react'
import { TbUserEdit } from 'react-icons/tb'
import { AiOutlineUsergroupAdd } from 'react-icons/ai'
import { GenericTable, TableColumn } from '@/components/Table/GenericTable'
import { useTable } from '@/components/Table/useTable'
import { useCallback, useMemo } from 'react'
import { UnifiedDriver } from '@/types/driver'
import { PermissionGuard } from '@/utils/permissions'
import { useAuthStore } from '@/stores/auth'
import { useTranslation } from 'react-i18next'

const getIdColumns = (selectedFilter: 'internal' | 'external' | 'linked' | 'all', t: (key: string) => string) => {
  if (selectedFilter === 'all' || selectedFilter === 'linked')
    return [
      {
        key: 'externalId',
        label: t('externalId'),
        visible: true,
        sortable: true,
      },
      {
        key: 'internalId',
        label: t('internalId'),
        visible: true,
        sortable: true,
      },
    ]
  if (selectedFilter === 'internal')
    return [
      {
        key: 'internalId',
        label: t('internalId'),
        visible: true,
        sortable: true,
      },
    ]

  return [
    {
      key: 'externalId',
      label: t('externalId'),
      visible: true,
      sortable: true,
    },
  ]
}

export function DriversGenericTable({
  drivers,
  onImportDriver,
  onLinkDriver,
  onEditDriver,
  selectedFilter,
}: {
  drivers: UnifiedDriver[]
  selectedFilter: 'internal' | 'external' | 'linked' | 'all'
  onImportDriver?: (driver: UnifiedDriver) => void
  onLinkDriver?: (driver: UnifiedDriver) => void
  onEditDriver?: (driver: UnifiedDriver) => void
}) {
  const { t } = useTranslation(['drivers', 'common'])
  const { userPermissions } = useAuthStore()

  const tableKey = 'drivers-generic-table'

  const filteredDrivers = useMemo(() => {
    if (selectedFilter === 'internal') return drivers.filter(driver => driver.type === 'internal')
    if (selectedFilter === 'external') return drivers.filter(driver => driver.type === 'external')
    if (selectedFilter === 'linked') return drivers.filter(driver => driver.type === 'linked')
    return drivers
  }, [drivers, selectedFilter])

  const table = useTable<UnifiedDriver>({
    data: filteredDrivers,
    tableKey,
    defaultSortField: 'name',
    defaultSortDirection: 'asc',
    defaultFilterField: 'name',
    itemsPerPage: 20,
    searchDebounceMs: 300,
    columns: [
      {
        key: 'name',
        label: t('name'),
        visible: true,
        sortable: true,
        render: (driver: UnifiedDriver) => (
          <Group gap="xs">
            <Text fw={500}>{driver.name}</Text>
          </Group>
        ),
      },
      {
        key: 'email',
        label: t('contact'),
        visible: true,
        sortable: true,
        render: (driver: UnifiedDriver) => (
          <div>
            {driver.email ? (
              <Text size="sm" c="dimmed">
                {driver.email}
              </Text>
            ) : (
              driver.phone && (
                <Text size="sm" c="dimmed">
                  {driver.phone}
                </Text>
              )
            )}
          </div>
        ),
      },
      {
        key: 'active',
        label: t('status'),
        visible: true,
        sortable: true,
        render: (driver: UnifiedDriver) => (
          <Badge color={driver.active ? 'green' : 'grey'} variant="light" size="sm">
            {driver.active ? t('active') : t('inactive')}
          </Badge>
        ),
      },
      {
        key: 'type',
        label: t('type'),
        visible: true,
        sortable: true,
        render: (driver: UnifiedDriver) => getStatusBadge(driver),
      },
      ...getIdColumns(selectedFilter, t),
      {
        key: 'canLink',
        label: t('actions'),
        visible: true,
        sortable: false,
        render: (driver: UnifiedDriver) => getActionButtons(driver),
      },
    ] as TableColumn<UnifiedDriver>[],
  })

  // Memoize handlers
  const handleImport = useCallback(
    (driver: UnifiedDriver) => {
      onImportDriver?.(driver)
    },
    [onImportDriver]
  )

  const handleLink = useCallback(
    (driver: UnifiedDriver) => {
      onLinkDriver?.(driver)
    },
    [onLinkDriver]
  )

  const handleEdit = useCallback(
    (driver: UnifiedDriver) => {
      onEditDriver?.(driver)
    },
    [onEditDriver]
  )

  // Memoize utility functions
  const getDriverType = useCallback(
    (driver: UnifiedDriver) => {
      if (driver.type === 'linked') {
        return { label: t('linked'), color: 'blue' }
      }
      if (driver.type === 'internal') {
        return { label: t('internal'), color: 'green' }
      }
      if (driver.type === 'external') {
        return { label: t('external'), color: 'cyan' }
      }
      return { label: t('unknown', { ns: 'common' }), color: 'gray' }
    },
    [t]
  )

  const getStatusBadge = useCallback(
    (driver: UnifiedDriver) => {
      const type = getDriverType(driver)
      return (
        <Badge color={type.color} variant="light" size="sm">
          {type.label}
        </Badge>
      )
    },
    [getDriverType]
  )

  const getActionButtons = useCallback(
    (driver: UnifiedDriver) => {
      const buttons = []

      if (driver.canImport) {
        buttons.push(
          <Tooltip key="import" label={t('importAsNewDriver')} arrowOffset={10} position="top" withArrow>
            <ActionIcon size={36} variant="default" aria-label={t('importDriver')} onClick={() => handleImport(driver)}>
              <AiOutlineUsergroupAdd size={20} color="var(--mantine-color-dimmed)" />
            </ActionIcon>
          </Tooltip>
        )
      }

      if (driver.canLink) {
        buttons.push(
          <Tooltip key="link" label={t('linkToExistingInternalDriver')} arrowOffset={10} position="top" withArrow>
            <ActionIcon size={36} variant="default" aria-label={t('linkDriver')} onClick={() => handleLink(driver)}>
              <IconLink size={20} color="var(--mantine-color-dimmed)" />
            </ActionIcon>
          </Tooltip>
        )
      }

      return (
        <PermissionGuard
          userPermissions={userPermissions}
          resource="drivers"
          action="write"
          fallback={<Text c="dimmed">{t('insufficientPermissions', { ns: 'common' })}</Text>}
        >
          <Group gap="xs" justify="left">
            {buttons.length > 0 ? (
              buttons
            ) : (
              <Tooltip key="edit" label={t('editDriver')} arrowOffset={10} position="top" withArrow>
                <ActionIcon size={36} variant="default" aria-label={t('editDriver')} onClick={() => handleEdit(driver)}>
                  <TbUserEdit size={20} color="var(--mantine-color-dimmed)" />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </PermissionGuard>
      )
    },
    [handleImport, handleLink, handleEdit, t, userPermissions]
  )

  return (
    <GenericTable
      {...table.tableProps}
      // Autocomplete options, CSV export and the disabled state derive from the full driver
      // set, while filtering/sorting/pagination operate on the type-filtered subset.
      data={drivers}
      getRowKey={(driver: UnifiedDriver) => driver.id}
      emptyMessage={t('noDriversFound')}
      stickyHeader={true}
      hideCsvDownload={false}
      csvFileName={`drivers-${selectedFilter}.csv`}
    />
  )
}
